import type { DiagnosticRow } from '@nowline/browser';
import { renderSource } from '@nowline/browser';
import type { NowOverride, PreviewHandle } from '@nowline/preview-shell';
import { mountPreview } from '@nowline/preview-shell';
import { useEffect, useRef, useState } from 'react';
import { type CanonicalOpts, canonicalExport } from './export/kernel.ts';
import { downloadExport } from './toolbar/export.ts';

// ~8-line debounce hook — no library dep.
function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}

// TODO: replace localToday(), nowToDate(), and nowToExportDate() with
// resolveToday({ now, zone }) from @nowline/browser after the 0.5.1 bump.
// The browser pipeline (renderSource) already defaults to local today when
// today is undefined, so the preview shows a now-line out of the box.

/** UTC-midnight of today's local civil date (host/viewer zone). */
function localToday(): Date {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

function nowToDate(now: NowOverride | undefined): Date | null | undefined {
    if (now === 'hide') return null;
    // undefined / 'today' → let the browser pipeline default to local today
    if (now === undefined || now === 'today') return undefined;
    // Bare YYYY-MM-DD — floating date; UTC midnight matches the layout engine's convention.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(now);
    if (m)
        return new Date(
            Date.UTC(parseInt(m[1]!, 10), parseInt(m[2]!, 10) - 1, parseInt(m[3]!, 10)),
        );
    return undefined;
}

/**
 * Convert a NowOverride to the `today` value the export kernel expects.
 * `'today'` / `undefined` → local civil today (shows the now-line).
 * `'hide'`                → `undefined` (suppress now-line in export).
 * `'YYYY-MM-DD'`          → explicit UTC midnight.
 */
function nowToExportDate(now: NowOverride | undefined): Date | undefined {
    if (now === 'hide') return undefined;
    if (now === undefined || now === 'today') return localToday();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(now);
    if (m)
        return new Date(
            Date.UTC(parseInt(m[1]!, 10), parseInt(m[2]!, 10) - 1, parseInt(m[3]!, 10)),
        );
    return localToday();
}

interface PreviewPaneProps {
    source: string;
    /**
     * Diagnostics published by the LSP worker. When any of them are
     * errors, the preview shell switches to the diagnostic table.
     */
    lspDiagnostics?: DiagnosticRow[];
    /**
     * Resolved app theme — 'light' or 'dark' (never 'auto').
     * Pushed to the preview-shell chrome via setMode; also forwarded
     * to renderSource as the diagram theme (header owns appearance).
     */
    resolvedTheme: 'light' | 'dark';
}

export function PreviewPane({ source, lspDiagnostics, resolvedTheme }: PreviewPaneProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<PreviewHandle | null>(null);
    // Stable refs so the once-created mount callbacks always read the latest values.
    const sourceRef = useRef(source);
    const nowRef = useRef<NowOverride | undefined>(undefined);
    const themeRef = useRef(resolvedTheme);
    const debouncedSource = useDebouncedValue(source, 100);
    const [now, setNow] = useState<NowOverride | undefined>(undefined);

    useEffect(() => {
        sourceRef.current = source;
    }, [source]);
    useEffect(() => {
        nowRef.current = now;
    }, [now]);
    useEffect(() => {
        themeRef.current = resolvedTheme;
    }, [resolvedTheme]);

    /** Build canonical export opts from the current stable-ref snapshot. */
    const exportOpts = (): CanonicalOpts => {
        const today = nowToExportDate(nowRef.current);
        return {
            ...(today !== undefined ? { today } : {}),
            locale: navigator.language,
            theme: themeRef.current,
        };
    };

    // Mount the preview shell once; wire canonical export paths and now-override callback.
    // Mode and render options are synced in dedicated effects — intentional empty dep array.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once; mode and render opts synced separately
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const handle = mountPreview(el, {
            mode: resolvedTheme,
            themeControl: 'hide',
            locale: navigator.language,
            initialFit: 'fitWidth',
            onSave: (req) => {
                if (req.format === 'png') {
                    // Canonical: re-rasterize via kernel (WASM + bundled fonts), ignore
                    // the canvas bytes the shell passes in req.body.
                    const opts = exportOpts();
                    void canonicalExport(sourceRef.current, 'png', opts).then((bytes) =>
                        downloadExport({ format: 'png', body: bytes }, sourceRef.current),
                    );
                } else {
                    // SVG is the engine's own output string — already canonical.
                    downloadExport(req, sourceRef.current);
                }
            },
            onCopyPngFallback: (_bytes) => {
                // Canonical fallback (ClipboardItem write failed or unavailable):
                // re-rasterize via kernel and trigger a file download. Ignore the
                // canvas bytes the shell passes.
                void canonicalExport(sourceRef.current, 'png', exportOpts()).then((kernelBytes) =>
                    downloadExport({ format: 'png', body: kernelBytes }, sourceRef.current),
                );
            },
            onViewOptions: ({ now: nowOverride }) => {
                if (nowOverride !== undefined) setNow(nowOverride);
            },
        });
        handleRef.current = handle;
        return () => {
            handle.dispose();
            handleRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Push chrome color scheme changes to the preview-shell after mount.
    useEffect(() => {
        handleRef.current?.setMode(resolvedTheme);
    }, [resolvedTheme]);

    // Re-render whenever the debounced source, theme, now-override, or LSP diagnostics change.
    useEffect(() => {
        const handle = handleRef.current;
        if (!handle) return;

        const lspErrors = (lspDiagnostics ?? []).filter((d) => d.severity === 'error');
        if (lspErrors.length > 0) {
            handle.setDiagnostics(lspErrors);
            return;
        }

        const today = nowToDate(now);
        let cancelled = false;
        void renderSource(debouncedSource, {
            theme: resolvedTheme,
            ...(today !== undefined ? { today } : {}),
            locale: navigator.language,
        }).then((result) => {
            if (cancelled) return;
            if (result.kind === 'svg') {
                handle.setSvg(result.svg);
            } else {
                handle.setDiagnostics(result.diagnostics);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [debouncedSource, lspDiagnostics, resolvedTheme, now]);

    return <div ref={rootRef} className='preview-fill' />;
}
